import {
  ArrowRight,
  CalendarClock,
  CirclePause,
  Eye,
  Play,
  Radio,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch } from "../../lib/api";
import { pickCurrentEvent, sortOpenEvents } from "@vzlive/shared";
import { useAdmin } from "./AdminShell";
import { AdminComposePage } from "./AdminComposePage";
import { AdminApprovalsPage } from "./AdminApprovalsPage";
import { AdminChatPage } from "./AdminChatPage";
import { StatusBadge } from "../../components/StatusBadge";
import { EventAlertPanels } from "../../components/admin/EventAlertPanels";
import type { AdminEvent } from "./types";

export function AdminLivedeskPage() {
  const { data, refresh } = useAdmin();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const openEvents = useMemo(() => sortOpenEvents(data.events), [data.events]);
  // Nur Owner/Admins dürfen zwischen Events wechseln – alle anderen Rollen
  // arbeiten automatisch am aktuellen Event.
  const canSwitch = data.role === "owner" || data.role === "admin";
  const active = useMemo(() => {
    const selected = searchParams.get("event");
    return (
      (selected
        ? openEvents.find((event) => event.id === selected)
        : undefined) ??
      pickCurrentEvent(data.events) ??
      data.events[0] ??
      null
    );
  }, [data.events, openEvents, searchParams]);

  const setStatus = async (event: AdminEvent, status: AdminEvent["status"]) => {
    setBusy(status);
    setError("");
    try {
      await apiFetch(`/api/admin/events/${event.id}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      });
      await refresh();
    } catch {
      setError(
        "Der Eventstatus konnte nicht geändert werden. Bitte versuche es erneut.",
      );
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="admin-page livedesk-page">
      <header className="admin-page-header">
        <div>
          <h1>Live-Desk</h1>
          <p>
            Hallo {data.user.name.split(" ")[0]}, hier läuft eure
            Berichterstattung zusammen.
          </p>
        </div>
        <span className="desk-clock">
          <i />
          {new Intl.DateTimeFormat(data.tenant.locale, {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: data.tenant.timezone,
          }).format(new Date())}
        </span>
      </header>

      {error && <p role="alert">{error}</p>}
      {active ? (
        <section className={`live-control live-control--${active.status}`}>
          <div className="live-control__status">
            <Radio size={26} />
            <div>
              <StatusBadge status={active.status} locale={data.tenant.locale} />
              <h2>{active.title}</h2>
              <small>
                {active.venue ||
                  new Intl.DateTimeFormat("de", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(active.startsAt))}
              </small>
            </div>
          </div>
          <div className="live-control__actions">
            {canSwitch &&
              active &&
              openEvents.length > 1 &&
              openEvents.some((event) => event.id === active.id) && (
                <label className="live-control__switcher">
                  <span>Event</span>
                  <select
                    value={active.id}
                    aria-label="Aktives Event wechseln"
                    onChange={(change) => {
                      setSearchParams(
                        { event: change.target.value },
                        { replace: true },
                      );
                    }}
                  >
                    {openEvents.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title} ·{" "}
                        {new Intl.DateTimeFormat(data.tenant.locale, {
                          day: "2-digit",
                          month: "2-digit",
                          timeZone: data.tenant.timezone,
                        }).format(new Date(event.startsAt))}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            {active.status === "scheduled" && (
              <button
                disabled={!!busy}
                onClick={() => void setStatus(active, "live")}
                className="button-live"
              >
                <Play size={17} fill="currentColor" /> Live starten
              </button>
            )}
            {active.status === "live" && (
              <button
                disabled={!!busy}
                onClick={() => void setStatus(active, "paused")}
              >
                <CirclePause size={17} /> Pausieren
              </button>
            )}
            {active.status === "paused" && (
              <button
                disabled={!!busy}
                onClick={() => void setStatus(active, "live")}
                className="button-live"
              >
                <Play size={17} fill="currentColor" /> Fortsetzen
              </button>
            )}
            <Link to="/admin/events">
              Event verwalten <ArrowRight size={17} />
            </Link>
            <a href={`/events/${active.slug}`} target="_blank" rel="noreferrer">
              <Eye size={17} /> Reader öffnen
            </a>
          </div>
        </section>
      ) : (
        <section className="admin-empty">
          <CalendarClock />
          <h2>Noch kein Event</h2>
          <p>
            Lege im <Link to="/admin/events">Event-Bereich</Link> das erste
            Event an.
          </p>
        </section>
      )}

      {active &&
        active.status !== "archived" &&
        active.status !== "removed" && (
          <>
            <div className="livedesk-bench">
              <aside className="livedesk-bench__approvals">
                <AdminApprovalsPage embedded eventId={active.id} />
              </aside>
              <div className="livedesk-bench__composer">
                <AdminComposePage
                  key={active.id}
                  embedded
                  initialEventId={active.id}
                />
              </div>
              <div className="livedesk-bench__chat">
                <AdminChatPage key={active.id} embedded eventId={active.id} />
              </div>
            </div>

            <section className="livedesk-extras">
              <header className="admin-page-header">
                <h2>Ausgabe-Extras · {active.title}</h2>
              </header>
              <EventAlertPanels key={active.id} eventId={active.id} />
            </section>
          </>
        )}

      {!active && (
        <p className="livedesk-blank">
          Sobald ein Event existiert, erscheinen hier Schreiben, Freigaben und
          Chat-Moderation.
        </p>
      )}
    </div>
  );
}
